import type { ReactNode } from 'react';
import type { ExternalToast } from 'sonner';

import { toast as sonnerToast } from 'sonner';

import { ToastCard, type ToastTone, type ToastButton } from './toast-card';

// ----------------------------------------------------------------------

export type ToastOptions = Omit<ExternalToast, 'description' | 'action' | 'cancel'> & {
  description?: ReactNode;
  action?: ToastButton;
  cancel?: ToastButton;
};

// Errors wait to be read and closed; a loading toast waits to be updated in place.
const PERSISTENT_TONES: ToastTone[] = ['error', 'loading'];

// Words that only repeat the tone. A toast titled with one leads with its description instead.
const TONE_ONLY_TITLES = ['Success', 'Failed', 'Failure', 'Error', 'Warning', 'Info'];

const MIN_READING_MS = 4000;
const BASE_READING_MS = 2000;
const READING_MS_PER_WORD = 250;
const MAX_READING_MS = 10000;

const wordCountOf = (node: ReactNode) =>
  typeof node === 'string' ? node.split(/\s+/).filter(Boolean).length : 0;

/** Long enough to read: 2s plus a quarter second a word, between 4 and 10 seconds. */
const readingTimeMs = (title: ReactNode, description: ReactNode) =>
  Math.min(
    MAX_READING_MS,
    Math.max(
      MIN_READING_MS,
      BASE_READING_MS + READING_MS_PER_WORD * (wordCountOf(title) + wordCountOf(description))
    )
  );

const isToneOnlyTitle = (title: ReactNode) =>
  typeof title === 'string' && TONE_ONLY_TITLES.includes(title);

const headlineOf = (title: ReactNode, description: ReactNode) =>
  isToneOnlyTitle(title) && description
    ? { title: description, description: undefined }
    : { title, description };

// The same words in the same tone are one toast: firing it again refreshes it instead of stacking.
const messageIdOf = (tone: ToastTone, title: ReactNode, description: ReactNode) =>
  typeof title === 'string' && (description === undefined || typeof description === 'string')
    ? `${tone}:${title}:${description ?? ''}`
    : undefined;

function showToast(tone: ToastTone, rawTitle: ReactNode, options: ToastOptions = {}) {
  const {
    description: rawDescription,
    icon,
    action,
    cancel,
    closeButton,
    ...sonnerOptions
  } = options;
  const { title, description } = headlineOf(rawTitle, rawDescription);
  const duration = PERSISTENT_TONES.includes(tone) ? Infinity : readingTimeMs(title, description);

  return sonnerToast.custom(
    (id) => (
      <ToastCard
        id={id}
        tone={tone}
        title={title}
        description={description}
        icon={icon}
        action={action}
        cancel={cancel}
        closeButton={closeButton}
      />
    ),
    { id: messageIdOf(tone, title, description), duration, ...sonnerOptions }
  );
}

type ShowToast = (title: ReactNode, options?: ToastOptions) => string | number;

const showTone =
  (tone: ToastTone): ShowToast =>
  (title, options) =>
    showToast(tone, title, options);

/**
 * The app's toasts, drawn as MUI cards inside `Snackbar`. It takes Sonner's arguments: a title,
 * then a description, an icon, an action and a cancel button, whether to show the close button,
 * a duration, and an id. Reusing an id updates that toast in place; without one, the same words
 * in the same tone count as the same toast. Unless a duration is passed, errors and loading
 * toasts stay until closed or updated and the rest stay as long as they take to read.
 */
export const toast = Object.assign(showTone('neutral'), {
  success: showTone('success'),
  info: showTone('info'),
  warning: showTone('warning'),
  error: showTone('error'),
  loading: showTone('loading'),
  dismiss: sonnerToast.dismiss,
});
